import { Box, InputAdornment, TextField } from "@mui/material"
import type { ReactNode } from "react"
import type { ControllerRenderProps, FieldError, FieldValues } from "react-hook-form"

import { FIELD_ERROR_CLASSES, FIELD_LABEL_BASE_CLASSES, FIELD_LABEL_FONT_CLASSES } from "@/constants/forms"

interface TextInputProps<TFieldValues extends FieldValues> {
  field: ControllerRenderProps<TFieldValues>
  disabled?: boolean
  label: string
  placeholder?: string
  required?: boolean
  maxLength?: number
  error?: FieldError | string
  startAdornment?: ReactNode
  endAdornment?: ReactNode
}

/**
 * @deprecated Use AerosTextField for uncontrolled usage or FormTextField for controlled usage.
 * @see AerosTextField
 * @see FormTextField
 */
export const TextInput = <TFieldValues extends FieldValues>({
  disabled = false,
  error,
  field,
  label,
  placeholder,
  maxLength,
  required = false,
  startAdornment,
  endAdornment,
}: TextInputProps<TFieldValues>): JSX.Element => {
  const errorMessage = typeof error === "string" ? error : error?.message

  return (
    <Box data-testid={field.name}>
      <Box className='mb-2 mt-6 flex w-full items-center justify-between gap-4'>
        <label htmlFor={field.name} className={FIELD_LABEL_BASE_CLASSES}>
          {label}
          {required && <span className='ml-1 text-red-600'>*</span>}
        </label>

        {maxLength && (
          <div className={`${FIELD_LABEL_FONT_CLASSES} text-gray-500`}>
            ({field.value?.length ?? 0}/{maxLength}) Characters
          </div>
        )}
      </Box>

      <TextField
        {...field}
        id={field.name}
        placeholder={placeholder}
        error={!!error}
        fullWidth
        variant='outlined'
        disabled={disabled}
        slotProps={{
          input: {
            startAdornment: startAdornment ? (
              <InputAdornment position='start'>{startAdornment}</InputAdornment>
            ) : undefined,
            endAdornment: endAdornment ? <InputAdornment position='end'>{endAdornment}</InputAdornment> : undefined,
          },
          htmlInput: { maxLength },
        }}
      />
      {errorMessage && <p className={FIELD_ERROR_CLASSES}>{errorMessage}</p>}
    </Box>
  )
}
